import { IS_STORAGE_AVAILABLE, LOCAL_STORAGE_MAP, URL_PARAM_MAP } from '@/setup/globals';
import i18n from '@/setup/i18n.js';
import useSessionStore from '@/stores/session';
import {
  currentUser,
  isInternalUser,
  isUserLoggedIn,
  sessionStoreInitialData,
} from '@/plugins/cdc/auth/auth-shared.js';
import { onLogin, openLogin, performPimcoreLogin } from '@/plugins/cdc/auth/cdc-login.js';
import { performCdcLogout, performLogout, pimcoreLogout } from '@/plugins/cdc/auth/cdc-logout.js';
import {
  getAccountInfo,
  getUserByPayload,
  isCdcAvailable,
  isLoggedIn,
  showErrorNotification,
  verifySession,
} from '@/plugins/cdc/shared.js';

const isLoginParameterSet = window.location.search.includes(`${URL_PARAM_MAP.cdcShowLoginWindow}=true`);

/**
 * Checks if the user is allowed to stay logged in based on defined requirements.
 *
 * - The user has to be logged in.
 * - The system needs to run in production mode.
 * - The user is no internal user.
 * - The user market and the site market match.
 */
function userFulfillsLoginRequirements() {
  const userMarket = currentUser?.market || '';

  if (
    isUserLoggedIn &&
    !isInternalUser &&
    String(userMarket).toUpperCase() !== String(sessionStoreInitialData.market).toUpperCase()
  ) {
    // We only log out from CDC, as everything else will be handled with the callbacks.
    performCdcLogout();
  }
}

/**
 * Calls the progressive profiling for CDC.
 */
function callProgressiveProfiling() {
  if (!IS_STORAGE_AVAILABLE || !localStorage.getItem(LOCAL_STORAGE_MAP.cdcShowProgressiveProfiling)) {
    return;
  }

  try {
    window.gigya.thisScript.globalConf.progressiveProfiling();
    localStorage.removeItem(LOCAL_STORAGE_MAP.cdcShowProgressiveProfiling);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

/**
 * CDC triggers events when a user is logged in or logged out.
 * This defines what should be triggered on our side when those events happen.
 */
function bindEventHandlersForCdcCallbacks() {
  window.gigya.accounts?.addEventHandlers({
    onLogin: (event) => {
      if (useSessionStore().isLoginEnabled && !isLoggedIn.value) {
        onLogin(event);
      }
    },
    onLogout: () => {
      if (isLoggedIn.value) {
        // eslint-disable-next-line no-console
        console.info('CDC - bindEventHandlersForCdcCallbacks - logout');

        performLogout();
      }
    },
  });
}

/**
 * Checks if the user is currently logged in at CDC and updates the other system according to the current status.
 *
 * @param {Boolean} isCurrentlyLoggedIn - Pimcore login status.
 */
function updateLoginStatusByCdc(isCurrentlyLoggedIn) {
  if (!sessionStoreInitialData.disableCdc && !isInternalUser) {
    const debugLogPrefix = 'CDC Error getAccountInfo:';

    verifySession()
      .then(() => {
        getAccountInfo({ include: 'profile,data' })
          .then((response) => {
            const responseHasUID = response?.UID;
            const loginEnabled = sessionStoreInitialData.isLoginEnabled;
            // Compare the internal user UID with the UID from CDC.
            const validUser = currentUser?.uid === response?.UID;

            if (isCurrentlyLoggedIn && (!responseHasUID || !loginEnabled || !validUser)) {
              const errors = [
                responseHasUID ? null : 'No UID given',
                loginEnabled ? null : 'Login disabled by pimcore.',
                validUser ? null : `Pimcore uid '${currentUser?.uid}' does not match CDC uid '${response?.UID}'`,
              ]
                .filter((message) => !!message)
                .join('; ');

              // eslint-disable-next-line no-console
              console.error(`${debugLogPrefix} ${errors}`);

              performLogout();
            }

            if (!isCurrentlyLoggedIn && responseHasUID && loginEnabled) {
              performPimcoreLogin(getUserByPayload(response));
            }
          })
          .catch((error) => {
            logoutOnCdcError(isCurrentlyLoggedIn, error);
          });
      })
      .catch((error) => {
        logoutOnCdcError(isCurrentlyLoggedIn, error);
      });
  }
}

/**
 * Starts the logout process for the user.
 *
 * @param {Boolean} isCurrentlyLoggedIn - Pimcore login status.
 * @param {String} error - The error that should be displayed.
 */
function logoutOnCdcError(isCurrentlyLoggedIn, error) {
  if (isCurrentlyLoggedIn) {
    // eslint-disable-next-line no-console
    console.error(`CDC Error: promise returned an error. - ${error}`);

    performLogout();
  }
}

/**
 * Define the plugin structure.
 */
export const cdcAuthPlugin = {
  openLogin,
  onLogout: performLogout,
  pimcoreLogout,
  logout: performCdcLogout,
};

function initCDC() {
  /**
   * Initialize the login process synchronously.
   */
  // Checks if cdc is enabled in the layout html and via the pimcore site settings.
  const isCdcEnabled = window.isCdcEnabled && window?.initialData?.session?.isLoginEnabled;
  // This is set in the SiteEnvironmentService.php and checks if there is any exception and the user is currently on eg a 404
  // page where there is no login provided.
  const isNoHttpErrorPage = !window?.initialData?.session?.disableCdc;

  if (isCdcEnabled && isNoHttpErrorPage && isCdcAvailable()) {
    bindEventHandlersForCdcCallbacks();
    userFulfillsLoginRequirements();
    callProgressiveProfiling();
    updateLoginStatusByCdc(isUserLoggedIn);

    if (!isUserLoggedIn && isLoginParameterSet) {
      openLogin();
    }
  } else if (isCdcEnabled && !isCdcAvailable()) {
    // eslint-disable-next-line no-console
    console.error('Gigya is not available!');
    showErrorNotification(i18n.global.t('globalMessages.loginUnavailable'));
  }
}

(function () {
  if (isCdcAvailable()) {
    initCDC();
  } else {
    setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('Gigya not loaded after: 250ms');
      initCDC();
    }, 250);
  }
})();
